import { useEffect, useRef } from "react";
import { useChatNotifications } from "./useChatNotifications";

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? value as Record<string, unknown> : {};

/** Chat sounds belong to their socket owner, never to a polled notification badge. */
export const useActivityNotifications = (notifications: readonly unknown[], account: { role: "user" | "vendor"; id: string }) => {
  const { notifyIncoming, rememberMessages } = useChatNotifications({ ...account, channel: "activity" });
  const seeded = useRef("");
  const mountedAt = useRef(Date.now());
  const identity = account.role + ":" + account.id;
  useEffect(() => {
    if (!account.id || !notifications.length) return;
    const relevant = notifications.map(record).filter(notification =>
      !notification.recipient || notification.recipient === account.id || record(notification.recipient)._id === account.id);
    const asMessage = (notification: Record<string, unknown>) => ({ id: String(notification._id || notification.id || ""), chatId: "activity", senderId: "platform-activity" });
    if (seeded.current !== identity) {
      seeded.current = identity;
      rememberMessages(relevant.map(asMessage));
      return;
    }
    for (const notification of relevant) {
      const type = typeof notification.type === "string" ? notification.type : "";
      const isCommunication = Boolean(record(notification.metadata).chatId) || /message|chat|support|customer.?care/i.test(type);
      const timestamp = typeof notification.createdAt === "string" ? Date.parse(notification.createdAt) : NaN;
      if (type && !isCommunication && Number.isFinite(timestamp) && timestamp >= mountedAt.current) notifyIncoming(asMessage(notification));
      else rememberMessages([asMessage(notification)]);
    }
  }, [account.id, identity, notifications, notifyIncoming, rememberMessages]);
};
